import type { Theme, Components } from "@mui/material/styles";

// ----------------------------------------------------------------------

export default function Table(theme: Theme): Components<Theme> {
  const headRadius = theme.shape.borderRadius;
  // Dark-mode background.neutral is translucent (alpha); sticky headers need opaque fill.
  const headBackground =
    theme.palette.mode === "dark"
      ? theme.palette.grey[700]
      : theme.palette.background.neutral;

  return {
    MuiTable: {
      styleOverrides: {
        root: {
          "&.MuiTable-stickyHeader": {
            borderCollapse: "separate",
            borderSpacing: 0,
          },
          // RouterLinkCell stretches a hit target over the row via
          // position:absolute; that only works if the <tr> is the containing block.
          "& tbody tr:has(.row-link)": {
            position: "relative",
            cursor: "pointer",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          height: 48,
          backgroundColor: headBackground,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "none",
        },
        head: {
          fontSize: theme.typography.pxToRem(14),
          lineHeight: 1.2,
          height: 48,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          color: theme.palette.text.secondary,
          fontWeight: theme.typography.fontWeightSemiBold,
          backgroundColor: headBackground,
          "&:first-of-type": {
            paddingLeft: theme.spacing(2),
            borderTopLeftRadius: headRadius,
            borderBottomLeftRadius: headRadius,
          },
          "&:first-of-type.MuiTableCell-paddingCheckbox": {
            paddingLeft: theme.spacing(1),
          },
          "&:last-of-type": {
            paddingRight: theme.spacing(2),
            borderTopRightRadius: headRadius,
            borderBottomRightRadius: headRadius,
            boxShadow: "none",
          },
        },
        sizeSmall: {
          "&.MuiTableCell-head": {
            height: 40,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
          },
          "&.MuiTableCell-body": {
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(0.75),
          },
        },
        stickyHeader: {
          top: 0,
          backgroundColor: headBackground,
          backgroundImage: "none",
          zIndex: 3,
          boxShadow: `1px 0 0 0 ${headBackground}`,
          "&:last-of-type": {
            boxShadow: "none",
          },
        },
        body: {
          paddingTop: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
          "&:first-of-type": {
            paddingLeft: theme.spacing(2),
          },
          "&:first-of-type.MuiTableCell-paddingCheckbox": {
            paddingLeft: theme.spacing(1),
          },
          "&:last-of-type": {
            paddingRight: theme.spacing(2),
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: `solid 1px ${theme.palette.divider}`,
        },
        toolbar: {
          height: 56,
        },
        select: {
          "&:focus": {
            borderRadius: theme.shape.borderRadius,
          },
        },
        selectIcon: {
          width: 20,
          height: 20,
          marginTop: -4,
        },
      },
    },
  };
}
